import React, { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import HelperCard from "@/components/HelperCard"
import { ChevronRight, Loader2 } from "lucide-react"
import { HelperProfile, JobPosting } from "@/types"
import { Link as RouterLink } from "react-router-dom"
import { apiService } from "@/services/api"
import { useAuth } from "@/contexts/AuthContext"

const PAGE_SIZE = 6

const TAB_TO_NATIONALITY: Record<string, string | undefined> = {
  all: undefined,
  philippines: "Philippines",
  indonesia: "Indonesia",
}

export function HelpersSection() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const isHelper = user?.role === "HELPER"

  const [activeTab, setActiveTab] = useState("all")
  const [helpers, setHelpers] = useState<HelperProfile[]>([])
  const [jobs, setJobs] = useState<JobPosting[]>([])
  const [loading, setLoading] = useState(true)

  const fetchHelpers = useCallback(async (tab: string) => {
    setLoading(true)
    try {
      const nationality = TAB_TO_NATIONALITY[tab]
      const res: any = await apiService.helpers.search({
        page: 0,
        size: PAGE_SIZE,
        sortBy: "createdAt",
        sortDirection: "DESC",
        isAvailable: true,
        ...(nationality ? { nationality } : {}),
      } as any)
      if (res?.success && res.data?.content) {
        setHelpers(res.data.content as HelperProfile[])
      }
    } catch (err) {
      console.error("Failed to load helpers", err)
    } finally {
      setLoading(false)
    }
  }, [])

  const fetchJobs = useCallback(async () => {
    setLoading(true)
    try {
      const res: any = await apiService.jobPostings.search({
        page: 0,
        size: PAGE_SIZE,
        sortBy: "createdAt",
        sortDirection: "DESC",
        status: "ACTIVE",
      } as any)
      if (res?.success && res.data?.content) {
        setJobs(res.data.content as JobPosting[])
      }
    } catch (err) {
      console.error("Failed to load jobs", err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (isHelper) {
      fetchJobs()
    } else {
      fetchHelpers(activeTab)
    }
  }, [isHelper, activeTab, fetchHelpers, fetchJobs])

  const formatSalary = (posting: JobPosting) => {
    if (posting.salaryType === 'FIXED' && posting.fixedSalary) {
      const currency = posting.salaryCurrency === 'HKD' ? 'HK$' : '$';
      return `${currency}${posting.fixedSalary.toLocaleString()}`;
    }
    if (posting.salaryType === 'RANGE' && posting.minSalary && posting.maxSalary) {
      const currency = posting.salaryCurrency === 'HKD' ? 'HK$' : '$';
      return `${currency}${posting.minSalary.toLocaleString()} - ${currency}${posting.maxSalary.toLocaleString()}`;
    }
    if (posting.salaryType === 'OTHER') {
      if (posting.salaryOtherOption === 'BASED_ON_EXPERIENCE') return t('jobPosting.salaryBasedOnExperience', '根據經驗');
      if (posting.salaryOtherOption === 'LEGAL_MINIMUM') return t('jobPosting.salaryLegalMinimum', '法定最低工資');
      if (posting.salaryOtherOption === 'TO_BE_DISCUSS') return t('jobPosting.salaryToBeDiscussed', '面議');
    }
    return t('jobPosting.salaryNotSpecified', '不指定');
  };

  if (isHelper) {
    return (
      <section id="helpers" className="bg-background py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6">
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-foreground md:text-3xl">{t('helpersSection.featuredJobs', '精選工作')}</h2>
            <p className="mt-2 text-muted-foreground">{t('helpersSection.featuredJobsSubtitle', '瀏覽最新發布的工作職位')}</p>
          </div>

          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>
          ) : jobs.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">{t('helpersSection.noJobs', '暫無工作職位')}</div>
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {jobs.map((job) => (
                <RouterLink key={job.id} to={`/job-postings/${job.id}/detail`}>
                  <div className="rounded-2xl border border-border/70 bg-background p-5 space-y-3 hover:border-primary/40 hover:bg-primary/5 transition cursor-pointer h-full">
                    <p className="font-semibold text-foreground leading-snug line-clamp-2">{job.headline || job.title}</p>
                    <p className="text-sm text-muted-foreground line-clamp-2">{job.description}</p>
                    <p className="text-sm font-medium text-foreground">{formatSalary(job)}</p>
                  </div>
                </RouterLink>
              ))}
            </div>
          )}

          <div className="mt-8 flex justify-center">
            <Button variant="outline" size="lg" className="group" asChild>
              <RouterLink to="/job-postings">
                {t('helpersSection.viewMoreJobs', '查看更多工作')}
                <ChevronRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </RouterLink>
            </Button>
          </div>
        </div>
      </section>
    )
  }

  return (
    <section id="helpers" className="bg-background py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6">
        <div className="mb-10 flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
          <div>
            <h2 className="text-2xl font-bold text-foreground md:text-3xl">{t('home.helpers.title')}</h2>
            <p className="mt-2 text-muted-foreground">{t('home.helpers.subtitle')}</p>
          </div>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              <TabsTrigger value="all">{t('home.helpers.tabs.all')}</TabsTrigger>
              <TabsTrigger value="philippines">{t('home.helpers.tabs.philippines')}</TabsTrigger>
              <TabsTrigger value="indonesia">{t('home.helpers.tabs.indonesia')}</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>
        ) : helpers.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">{t('home.noCandidates', 'No candidates available')}</div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {helpers.map((helper) => (
              <HelperCard key={helper.id} helper={helper} />
            ))}
          </div>
        )}

        <div className="mt-8 flex justify-center">
          <Button variant="outline" size="lg" className="group" asChild>
            <RouterLink to="/candidates">
              {t('home.helpers.viewMore')}
              <ChevronRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </RouterLink>
          </Button>
        </div>
      </div>
    </section>
  )
}
